import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert,
  CircularProgress, Box, Typography
} from '@mui/material';

// Maternity letters (LETTER-MAT-001 to 006). Pre-filled from the maternity request; the manager checks the
// dates and creates the letter, which is saved to the Document Vault.
//   <MaternityLetterDialog open={!!sel} requestId={sel?.id} letterKey={sel?.key} letterLabel={sel?.label} onClose={...} />
const FIELDS_BY_LETTER = {
  'acknowledgement': ['notification_date', 'ewc', 'return_date'],
  'return-date': ['return_date'],
  'kit-day': ['kit_day_date', 'kit_day_hours', 'kit_day_purpose', 'kit_days_remaining', 'kit_response_date'],
  'extension-reminder': ['return_date'],
  'return-reminder': ['return_date'],
  'urgent-return': ['return_date', 'previous_letter_date', 'response_deadline'],
};

const LABELS = {
  notification_date: 'Date she told you about the pregnancy',
  ewc: 'Expected week of childbirth (due date)',
  return_date: 'Expected return date',
  kit_day_date: 'KIT day date',
  kit_day_hours: 'Expected hours',
  kit_day_purpose: 'Purpose of the KIT day',
  kit_days_remaining: 'KIT days she has left (of 10)',
  kit_response_date: 'Reply needed by',
  previous_letter_date: 'Date of your previous letter',
  response_deadline: 'Reply needed by',
};
const DATE_FIELDS = ['notification_date', 'ewc', 'return_date', 'kit_day_date', 'kit_response_date',
  'previous_letter_date', 'response_deadline'];

const show = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function MaternityLetterDialog({ open, requestId, letterKey, letterLabel, onClose }) {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !requestId) return;
    let cancelled = false;
    setLoading(true); setError(''); setDone(null); setPrefill(null);
    api.get(`/letter-gen/prefill/maternity/${requestId}`)
      .then((res) => {
        if (cancelled) return;
        const { warnings, employee_name, ...editable } = res.data.prefill;
        setPrefill(res.data.prefill);
        setFields(editable);
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || 'Could not load this maternity leave.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, requestId]);

  const set = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.value }));

  const handleGenerate = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/letter-gen/generate/maternity/${letterKey}/${requestId}`, fields);
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the letter.');
    } finally {
      setSaving(false);
    }
  };

  const names = FIELDS_BY_LETTER[letterKey] || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{letterLabel || 'Maternity letter'}{prefill ? ` - ${prefill.employee_name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {done ? (
          <Alert severity="success">
            {done.message}.{' '}
            <Button size="small" onClick={() => { onClose(); navigate('/documents'); }}>Open Document Vault</Button>
          </Alert>
        ) : prefill && (
          <>
            {prefill.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            {letterKey === 'urgent-return' && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Send this letter by recorded delivery. Use it only if the return date is one she gave you in writing,
                or the end of her full 52 weeks. Until then she is entitled to be on leave.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Leave starts {show(prefill.leave_start)}. Ordinary maternity leave ends {show(prefill.oml_end)} and
              additional leave ends {show(prefill.aml_end)}. These come from the leave request. The other dates in
              the letter are worked out from them.
            </Typography>
            <Grid container spacing={2}>
              {names.map((name) => (
                <Grid item xs={12} sm={name === 'kit_day_purpose' ? 12 : 6} key={name}>
                  <TextField
                    fullWidth size="small" label={LABELS[name]} value={fields[name] ?? ''} onChange={set(name)}
                    type={DATE_FIELDS.includes(name) ? 'date' : 'text'}
                    InputLabelProps={DATE_FIELDS.includes(name) ? { shrink: true } : undefined}
                    multiline={name === 'kit_day_purpose'}
                    helperText={name === 'return_date'
                      ? 'Leave as it is unless she has given you a different date in writing. The default is the end of her full 52 weeks.'
                      : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={handleGenerate} disabled={saving || loading || !prefill}>
            {saving ? 'Creating...' : 'Create letter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
